import { useEffect } from "react";
import { effectiveLayout, useLayout } from "./layout.js";
import { useSession } from "../state/session.js";
import { currentHours, getTimeSetting } from "./look/timeStore.js";
import { WorldCanvas } from "./WorldCanvas.js";
import { WorldPanel } from "./WorldPanel.js";
import { getWorldFocus, worldStore } from "./store.js";
import "./world.css";

/**
 * The Dünya page body: the 3D viewport next to the side panel (stacked on phones). It connects
 * the shared world store to the realtime client for as long as the page is on screen: the
 * gateway streams a snapshot and then deltas, and the store resumes by `worldSeq` after a drop.
 */
export function WorldView() {
  const { client } = useSession();
  const docked = effectiveLayout(useLayout()) === "docked";
  // The org tree feeds the floor list and the doors; keep it loaded with or without the panel.
  useEffect(() => worldStore.retain(), []);
  useEffect(() => {
    worldStore.setPageActive(true);
    const detach = client?.attachWorld(worldStore);
    return () => {
      worldStore.setPageActive(false);
      detach?.();
    };
  }, [client]);
  useEffect(() => {
    if (import.meta.env["VITE_UZ_MOCK"] !== "1") return;
    // Test hook, mock mode only: e2e selects parts without pixel-picking and reads the store.
    const hook = {
      select: (id: string | null, part?: string) =>
        worldStore.select(id ? { id, ...(part ? { part } : {}) } : null),
      node: (id: string) => worldStore.node(id) ?? null,
      focus: () => getWorldFocus(),
      /** The sky's setting (`auto` or an hour) and the hour it means right now. */
      timeOfDay: () => ({ setting: getTimeSetting(), hours: currentHours() }),
      state: () => {
        const s = worldStore.getState();
        return { status: s.status, worldSeq: s.worldSeq, nodes: worldStore.nodes.size };
      },
    };
    (globalThis as { __uzWorld?: typeof hook }).__uzWorld = hook;
    return () => {
      delete (globalThis as { __uzWorld?: typeof hook }).__uzWorld;
    };
  }, []);
  return (
    <div className={`world-view${docked ? " is-docked" : ""}`}>
      <WorldCanvas />
      {docked ? <WorldPanel /> : null}
    </div>
  );
}

export { WorldCanvas, WorldPanel };
export { getWorldFocus, worldStore } from "./store.js";
export default WorldCanvas;
